import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import {
  BillingFormFields,
  BillingFormSection,
} from "../../NormasTeachingCheckout/BillingFormSection.tsx";
import {
  bonificoBillingSchemaFields,
  hasSdiOrPec,
} from "../../BonificoCheckout/bonificoBillingValidation.ts";
import { BusinessType } from "../../MoneyTransfer/BusinessType.tsx";
import { useCreateBonificoOrder } from "../../ProductService/BonificoOrder/useCreateBonificoOrder.ts";
import {
  toBonificoBusinessInformation,
  toBonificoCustomerTaxCode,
} from "../../ProductService/BonificoOrder/toBonificoBusinessInformation.ts";
import { ItalianBillingCountry } from "../../MoneyTransfer/billingCountries.ts";
import { getBonificoOrderErrorMessage } from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { B2BCourseGrantsEditor } from "./B2BCourseGrantsEditor.tsx";
import {
  B2BCourseGrantDraft,
  B2BCourseGrantDraftErrors,
  hasCourseGrantDraftErrors,
  toCourseGrantPayloads,
  validateCourseGrantDrafts,
} from "./b2bCourseGrantDrafts.ts";

type FormValues = BillingFormFields & {
  customer: { email: string; firstName: string; lastName: string };
  contractDate: string;
  contractAmountInEuros: number;
};

const CONTACT_FIELD_LABELS = {
  email: "Email",
  firstName: "Nome",
  lastName: "Cognome",
} as const;

const schema = yup
  .object({
    customer: yup.object({
      email: yup
        .string()
        .trim()
        .email("Email non valida")
        .required("Obbligatorio"),
      firstName: yup.string().trim().required("Obbligatorio"),
      lastName: yup.string().trim().required("Obbligatorio"),
    }),
    contractDate: yup.string().required("La data del contratto è obbligatoria"),
    contractAmountInEuros: yup
      .number()
      .typeError("Inserisci un importo valido")
      .moreThan(0, "L'importo deve essere maggiore di zero")
      .required("L'importo del contratto è obbligatorio"),
    ...bonificoBillingSchemaFields,
  })
  .test("has-at-least-sdi-or-pec", "", hasSdiOrPec);

export function B2BBonificoOrderForm({
  onSaved,
  onCancel,
}: {
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { createB2BOrder, loading } = useCreateBonificoOrder();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [courseGrants, setCourseGrants] = useState<B2BCourseGrantDraft[]>([]);
  const [courseGrantErrors, setCourseGrantErrors] = useState<
    B2BCourseGrantDraftErrors[]
  >([]);
  const {
    control,
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<FormValues>,
    defaultValues: {
      customer: { email: "", firstName: "", lastName: "" },
      invoiceRequired: true,
      businessType: BusinessType.Company,
      address: {
        street: "",
        cap: "",
        city: "",
        province: "",
        country: ItalianBillingCountry.Italy,
      },
      contractDate: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    const grantErrors = validateCourseGrantDrafts(courseGrants);
    setCourseGrantErrors(grantErrors);
    if (hasCourseGrantDraftErrors(grantErrors)) return;
    try {
      await createB2BOrder({
        customer: {
          ...values.customer,
          taxCode: toBonificoCustomerTaxCode(values),
        },
        businessInformation: toBonificoBusinessInformation(values),
        contractDate: values.contractDate,
        contractAmountInCents: Math.round(values.contractAmountInEuros * 100),
        courseGrants: toCourseGrantPayloads(courseGrants),
      });
      onSaved();
    } catch (e) {
      setSubmitError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante la creazione dell'ordine B2B. Riprova.",
        ),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Contratto</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Data del contratto *
            </label>
            <input
              type="date"
              max={dayjs().format("YYYY-MM-DD")}
              {...register("contractDate")}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700"
            />
            {errors.contractDate && (
              <p className="text-xs text-red-500">
                {errors.contractDate.message}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Importo del contratto (€) *
            </label>
            <TextInput
              type="number"
              step="0.01"
              min="0"
              {...register("contractAmountInEuros")}
              errorMessage={errors.contractAmountInEuros?.message}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Referente</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(["email", "firstName", "lastName"] as const).map((key) => (
            <div key={key} className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {CONTACT_FIELD_LABELS[key]} *
              </label>
              <TextInput
                {...register(`customer.${key}`)}
                errorMessage={errors.customer?.[key]?.message}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Dati di fatturazione</h2>
        <BillingFormSection
          control={control}
          register={register}
          errors={errors}
          invoiceRequired={true}
          businessType={watch("businessType")}
          taxCode={watch("taxCode")}
          isBusinessInformationRequired={true}
          isInvoiceMandatory={true}
          trigger={trigger}
          askBillingCountry
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <div>
          <h2 className="font-semibold text-lg">Corsi da erogare</h2>
          <p className="text-xs text-gray-500">
            Facoltativo: puoi erogare i corsi anche in seguito dal dettaglio
            dell'ordine.
          </p>
        </div>
        <B2BCourseGrantsEditor
          value={courseGrants}
          onChange={setCourseGrants}
          errors={courseGrantErrors}
        />
      </div>

      <p className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
        L'ordine assegna solo i corsi indicati e non chiude deal su HubSpot.
        Ogni pagamento registrato inserisce solo la transazione su BigQuery,
        senza emettere fattura.
      </p>

      {submitError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {submitError}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
        >
          Annulla
        </button>
        <PrimaryButton
          label="Crea ordine B2B"
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          disabled={loading}
        />
      </div>
    </form>
  );
}
